import { AlertTriangle, CheckCircle2, Circle, XCircle } from "lucide-react";
import { GATE_STATE_META, gateCounts, type Gate, type GateCounts } from "../lib/qa";

/**
 * THE HONEST QA INSTRUMENT — the core contract of this product.
 *
 * GateMeter takes {passed, failed, notRun} and CANNOT express a percentage. There is no prop
 * for one, no number derived inside it, and no Math.max floor anywhere near it. It renders a
 * segmented track and a LITERAL COUNT:
 *
 *     "0 of 6 checks run — final QA has not been run yet"
 *
 * While QA is running it shows an indeterminate shimmer and no number at all, because there
 * is no honest number to show. The only percentage this product may ever print is 100%, and
 * only from a real backend pass.
 *
 * A "not run" gate renders as a HOLLOW RING and the words "Not run". Never a checkmark. The
 * check glyph is drawn from `gate.state === "passed"`, and the Gate type makes that state
 * unconstructible without the backend QaLevel that produced it.
 */

export function GateMeter({ gates, running }: { gates: Gate[]; running: boolean }) {
  const counts = gateCounts(gates);
  return (
    <div className="gate-meter" data-testid="gate-meter" data-running={running ? "true" : "false"}>
      <div
        className="gate-track"
        role="img"
        aria-label={gateCountSentence(counts, running)}
        data-running={running ? "true" : "false"}
      >
        {running
          ? <i className="gate-seg gate-seg-running" />
          : gates.map((gate) => (
              <i
                className="gate-seg"
                data-state={gate.state}
                key={gate.id}
              />
            ))}
      </div>
      <p className="gate-count">{gateCountSentence(counts, running)}</p>
    </div>
  );
}

/**
 * The sentence. Every branch states what is TRUE, and says "not yet" when nothing has run.
 * There is no branch that can produce a fabricated figure.
 */
export function gateCountSentence(counts: GateCounts, running: boolean) {
  if (running) return `Checking ${counts.total} checks — no result yet`;
  // "run yet" is bound by a non-breaking space so "yet" can never orphan onto its own line
  // in the narrow desktop QA rail. Same words, same meaning — typography only.
  if (!counts.ran) return `0 of ${counts.total} checks run — final QA has not been run\u00A0yet`;
  if (counts.passed === counts.total) return `${counts.total} of ${counts.total} checks passed`;
  if (counts.failed) {
    return `${counts.ran} of ${counts.total} checks run — ${counts.failed} did not pass, ${counts.notRun} not run`;
  }
  return `${counts.ran} of ${counts.total} checks run — ${counts.notRun} not run`;
}

function GateGlyph({ gate }: { gate: Gate }) {
  if (gate.state === "passed") return <CheckCircle2 aria-hidden="true" />;
  if (gate.state === "failed" || gate.state === "blocked") return <XCircle aria-hidden="true" />;
  // Never colour alone: a warning gets its OWN glyph, so it is distinguishable from a blocker without
  // relying on hue. A reader who cannot see the amber must still see that this is not an X.
  if (gate.state === "warned") return <AlertTriangle aria-hidden="true" />;
  // not_run / running / not_applicable — a HOLLOW RING. Never a tick.
  return <Circle aria-hidden="true" />;
}

/**
 * The six gates, each stating its own state in the six permitted words, and each carrying
 * its OWN blocking reason INSIDE the row that is blocking. The reason is never exiled to a
 * separate "What needs attention" card that the reviewer has to correlate by hand, and it is
 * never hidden behind a closed disclosure — this list is always rendered open.
 */
export function GateList({ gates }: { gates: Gate[] }) {
  return (
    <ul className="gate-list" data-testid="qa-checks">
      {gates.map((gate) => {
        const meta = GATE_STATE_META[gate.state];
        return (
          <li
            className="gate-row"
            data-testid="qa-level"
            data-level-id={gate.id}
            data-status={gate.state}
            data-tone={meta.tone}
            key={gate.id}
          >
            <span className="gate-row-glyph" data-tone={meta.tone}>
              <GateGlyph gate={gate} />
            </span>
            <div className="gate-row-copy">
              <strong>{gate.label}</strong>
              <span className="gate-row-state">{meta.label}</span>
              {gate.messages.length ? (
                <ul className="gate-row-reasons">
                  {gate.messages.map((message) => (
                    <li key={message}>{message}</li>
                  ))}
                </ul>
              ) : null}
            </div>
          </li>
        );
      })}
    </ul>
  );
}
